import {
  PlaybookChatTypes,
  PlaybookEditorTypes,
  PlaybookEmailTypes,
  PlaybookFastlaneTypes,
  PlaybookLandingPageTypes,
  PlaybookMarketplaceWebTypes,
  PlaybookMigratedTypes,
  PlaybookReportType,
} from '@driftt/api-types'

import { SupportedPlaybookTypes } from 'types/Playbook'

export type ReportTypeOptions =
  | PlaybookEmailTypes
  | PlaybookEditorTypes
  | PlaybookMigratedTypes
  | PlaybookFastlaneTypes
  | PlaybookReportType
  | PlaybookChatTypes
  | PlaybookMarketplaceWebTypes

export const REPORT_TYPE_TO_TABLE_NAME: Readonly<Record<string, string>> =
  Object.freeze({
    [PlaybookReportType.BLOG_NURTURE]: 'Triggered email',
    [PlaybookReportType.MARKETING_SEQUENCE]: 'Triggered email',
    [PlaybookReportType.SEND_AN_AUTOMATED_EMAIL]: 'Triggered email',
    [PlaybookReportType.ABANDONED_BOOK_A_MEETING]: 'Triggered email',
    [PlaybookReportType.NEWSLETTER]: 'One-time email',
    [PlaybookReportType.NEW_BLOG_CONTENT]: 'One-time email',
    [PlaybookReportType.PRODUCT_ANNOUNCEMENT]: 'One-time email',
    [PlaybookReportType.SEND_A_ONETIME_EMAIL]: 'One-time email',
    [PlaybookReportType.MARKETING_ONE_TIME_SEQUENCE]: 'One-time email',
    [PlaybookReportType.OUTBOUND_SEQUENCE]: 'Sales email sequence',
    [PlaybookReportType.NPS_BOT]: 'Bot',
    [PlaybookReportType.EMPTY_LEADBOT]: 'Bot',
    [PlaybookReportType.BOOK_MORE_MEETINGS]: 'Bot',
    [PlaybookReportType.MORE_QUALIFIED_CONVERSATIONS]: 'Bot',
    [PlaybookReportType.LANDING_PAGE_LEADBOT]: 'Bot',
    [PlaybookReportType.VSB_DEFAULT_PLAYBOOK]: 'Bot',
    [PlaybookReportType.BUILD_A_SUBSCRIBER_LIST]: 'Email capture',
    [PlaybookReportType.ABM]: 'ABM welcome message',
    [PlaybookReportType.EMPTY_WELCOME_MESSAGE]: 'Welcome message',
    [PlaybookReportType.MORE_SALES_CONVERSATIONS]: 'Live chat',
    [PlaybookReportType.TALK_TO_YOUR_OUTBOUND_PROSPECTS]:
      'Outbound welcome message',
    [PlaybookReportType.EMPTY_SLIDER]: 'Slider message',
    [PlaybookReportType.DRIVE_TRAFFIC_TARGETED_MESSAGE]: 'Slider',
    [PlaybookReportType.EMPTY_TAKEOVER]: 'Full screen takeover',
    [PlaybookReportType.PROMOTION_FULL_SCREEN_TAKEOVER]: 'Full screen takeover',
    [PlaybookReportType.CONVERSATION_STARTER_BOT]: 'Bot',
    [PlaybookReportType.RETARGETING_BOT]: 'Bot',
    [PlaybookReportType.PRICING_PAGE_BOT]: 'Bot',
    [PlaybookReportType.PRICING_PAGE_BIONIC_BOT]: 'Bot',
    [PlaybookReportType.SECOND_NET_BOT]: 'Bot',
    [PlaybookReportType.ABM_HOMEPAGE_BOT]: 'Bot',
    [PlaybookReportType.PAID_TRAFFIC_BOT]: 'Bot',
    [PlaybookReportType.FORM_REPLACER_CLP]: 'Bot',
    [PlaybookReportType.CONVO_CONTENT_CLP]: 'Bot',
    [PlaybookReportType.AI_TOPICS]: 'Bot',
    [PlaybookReportType.SIGNATURE]: 'Signature',
    [PlaybookReportType.ENTERPRISE_BOT]: 'Bot',
    [PlaybookReportType.FORM_SUBMISSION_FAST_LANE]: 'Fastlane',
    [PlaybookReportType.ENGAGE_ALL_BOT]: 'Bot',
    [PlaybookReportType.ENGAGE_ALL_BIONIC_BOT]: 'Bot',
    [PlaybookReportType.WELCOME_MESSAGE_V3]: 'Bot',
    [PlaybookReportType.SKIP_THE_FORM_BOT]: 'Bot',
    [PlaybookReportType.VISITOR_RETARGETING_BOT]: 'Bot',
    [PlaybookReportType.BLOG_BOT]: 'Bot',
    [PlaybookReportType.PAID_AD_CONVERTER_BOT]: 'Bot',
    [PlaybookReportType.TARGET_ACCOUNTS_BOT]: 'Bot',
  })

export const LANDING_PAGE_REPORT_TYPES: Readonly<PlaybookLandingPageTypes[]> =
  Object.freeze(Object.values(PlaybookLandingPageTypes))

export const CHAT_PLAYBOOKS_REPORT_TYPES: Readonly<PlaybookChatTypes[]> =
  Object.freeze(Object.values(PlaybookChatTypes))

export const FAST_LANE_REPORT_TYPES: Readonly<PlaybookFastlaneTypes[]> =
  Object.freeze(Object.values(PlaybookFastlaneTypes))

export const ALL_CHAT_PLAYBOOK_TYPES: Readonly<ReportTypeOptions[]> =
  Object.freeze([...CHAT_PLAYBOOKS_REPORT_TYPES, ...FAST_LANE_REPORT_TYPES])

export const MARKETPLACE_WEB_REPORT_TYPES: Readonly<
  PlaybookMarketplaceWebTypes[]
> = Object.freeze(Object.values(PlaybookMarketplaceWebTypes))

export const CHAT_USER_PLAYBOOK_TYPES = Object.freeze(['SEQUENCE'])

export const EMAIL_PLAYBOOK_TYPES: Readonly<PlaybookEmailTypes[]> =
  Object.freeze(Object.values(PlaybookEmailTypes))

export const MIGRATED_PLAYBOOK_TYPES: Readonly<PlaybookMigratedTypes[]> =
  Object.values(PlaybookMigratedTypes)

export const PLAYBOOK_EDITOR_TYPES: Readonly<PlaybookEditorTypes[]> =
  Object.freeze(Object.values(PlaybookEditorTypes))

export const LANDING_PAGE_PLAYBOOK_REPORT_TYPES: Readonly<
  PlaybookLandingPageTypes[]
> = Object.freeze(Object.values(PlaybookLandingPageTypes))

export const REPORT_TYPES_TO_TYPES: Readonly<
  Record<string, SupportedPlaybookTypes>
> = Object.freeze({
  [PlaybookReportType.MEETING_BOOKING_BOT]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.LANDING_PAGE_LEADBOT]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.BOOK_MORE_MEETINGS]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.MORE_QUALIFIED_CONVERSATIONS]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.EMPTY_LEADBOT]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.VSB_DEFAULT_PLAYBOOK]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.CONVERSATION_STARTER_BOT]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.RETARGETING_BOT]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.PRICING_PAGE_BOT]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.PRICING_PAGE_BIONIC_BOT]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.SECOND_NET_BOT]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.ABM_HOMEPAGE_BOT]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.PAID_TRAFFIC_BOT]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.FORM_REPLACER_CLP]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.CONVO_CONTENT_CLP]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.ENTERPRISE_BOT]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.FORM_SUBMISSION_FAST_LANE]: SupportedPlaybookTypes.BOT,
  [PlaybookReportType.AI_TOPICS]: SupportedPlaybookTypes.BOT,
  ENGAGE_ALL_BOT: SupportedPlaybookTypes.BOT,
  ENGAGE_ALL_BIONIC_BOT: SupportedPlaybookTypes.BOT,
  WELCOME_MESSAGE_V3: SupportedPlaybookTypes.BOT,
  SKIP_THE_FORM_BOT: SupportedPlaybookTypes.BOT,
  VISITOR_RETARGETING_BOT: SupportedPlaybookTypes.BOT,
  BLOG_BOT: SupportedPlaybookTypes.BOT,
  PAID_AD_CONVERTER_BOT: SupportedPlaybookTypes.BOT,
  TARGET_ACCOUNTS_BOT: SupportedPlaybookTypes.BOT,

  [PlaybookReportType.NPS_BOT]: SupportedPlaybookTypes.NOT_SUPPORTED, //'INTERACTION'

  [PlaybookReportType.BUILD_A_SUBSCRIBER_LIST]:
    SupportedPlaybookTypes.NOT_SUPPORTED, //'EMAIL_CAPTURE'

  [PlaybookReportType.MARKETING_SEQUENCE]: SupportedPlaybookTypes.NOT_SUPPORTED, //'EMAIL'
  [PlaybookReportType.MARKETING_ONE_TIME_SEQUENCE]:
    SupportedPlaybookTypes.NOT_SUPPORTED, //'EMAIL'
  [PlaybookReportType.OUTBOUND_SEQUENCE]: SupportedPlaybookTypes.NOT_SUPPORTED, //'EMAIL'
  [PlaybookReportType.BLOG_NURTURE]: SupportedPlaybookTypes.NOT_SUPPORTED, //'EMAIL'
  [PlaybookReportType.NEWSLETTER]: SupportedPlaybookTypes.NOT_SUPPORTED, //'EMAIL'
  [PlaybookReportType.NEW_BLOG_CONTENT]: SupportedPlaybookTypes.NOT_SUPPORTED, //'EMAIL'
  [PlaybookReportType.PRODUCT_ANNOUNCEMENT]:
    SupportedPlaybookTypes.NOT_SUPPORTED, //'EMAIL'

  [PlaybookReportType.EMPTY_TAKEOVER]: SupportedPlaybookTypes.NOT_SUPPORTED, //'TAKEOVER'
  [PlaybookReportType.PROMOTION_FULL_SCREEN_TAKEOVER]:
    SupportedPlaybookTypes.NOT_SUPPORTED, //'TAKEOVER'

  [PlaybookReportType.MORE_SALES_CONVERSATIONS]:
    SupportedPlaybookTypes.NOT_SUPPORTED, //'SLIDER'
  [PlaybookReportType.EMPTY_SLIDER]: SupportedPlaybookTypes.NOT_SUPPORTED, //'SLIDER'

  [PlaybookReportType.DRIVE_TRAFFIC_TARGETED_MESSAGE]:
    SupportedPlaybookTypes.NOT_SUPPORTED, //'WELCOME_MESSAGE'
  [PlaybookReportType.TALK_TO_YOUR_OUTBOUND_PROSPECTS]:
    SupportedPlaybookTypes.NOT_SUPPORTED, //'WELCOME_MESSAGE'
  [PlaybookReportType.EMPTY_WELCOME_MESSAGE]:
    SupportedPlaybookTypes.NOT_SUPPORTED, //'WELCOME_MESSAGE'
  [PlaybookReportType.ABM]: SupportedPlaybookTypes.NOT_SUPPORTED, //'WELCOME_MESSAGE'
})

export const CLP_AS_PLAYBOOK_RELEASE_TIME = 1554125400161

export const CLP_PLAYBOOK_TYPE = 'Landing page'
